import { api } from '../lib/api';

export interface BottlingContainerOption {
  id: number;
  containerCode: string;
  siteId: number;
  siteName: string;
  productId: number;
  productName: string;
  waterPercent: number | null;
  remainingKg: number;
}

export interface BottlingMeta {
  containers: BottlingContainerOption[];
  products: Array<{ id: number; name: string; netWeightG: number | null; kind: string }>;
  sites: Array<{ id: number; name: string }>;
  packagingComponents: Array<{
    productId: number;
    packagingProductId: number;
    packagingProductName: string;
    qtyPerUnit: number;
  }>;
  packagingStock: Array<{
    sourceType: 'LEGACY' | 'CONTAINER';
    productId: number;
    productName: string;
    batchCode: string | null;
    siteId: number;
    siteName: string;
    qtyOnHand: number;
  }>;
}

export interface BottlingListRow {
  id: number;
  bottlingCode: string;
  bottlingDate: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  sourceContainerId: number;
  sourceBlendId: number | null;
  sourceContainerCode: string;
  sourceWaterPercentSnapshot: number | null;
  sourceNectarItemIdSnapshot: number | null;
  sourceNectarLabel: string | null;
  sourceSiteId: number;
  sourceSiteName: string;
  targetSiteId: number;
  targetSiteName: string;
  productId: number | null;
  productName: string | null;
  qtyUnits: number;
  totalKg: number;
  batchCode: string | null;
  lineCount: number;
  productLines: Array<{
    productId: number;
    productName: string;
    qtyUnits: number;
  }>;
}

export interface BottlingDetails {
  id: number;
  bottlingCode: string;
  bottlingDate: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  consumeTxId: number | null;
  produceTxId: number | null;
  sourceContainerId: number;
  sourceBlendId: number | null;
  sourceContainerCode: string;
  sourceWaterPercentSnapshot: number | null;
  sourceNectarItemIdSnapshot: number | null;
  sourceNectarLabel: string | null;
  sourceSiteId: number;
  sourceSiteName: string;
  targetSiteId: number;
  targetSiteName: string;
  totalUnits: number;
  totalKg: number;
  sourceContainers: Array<{
    containerId: number;
    containerCode: string;
    siteId: number;
    siteName: string;
    productId: number;
    productName: string;
    qtyKg: number;
    sortOrder: number;
  }>;
  lines: Array<{
    id: number;
    bottlingLineId: number | null;
    productId: number;
    productName: string;
    qtyUnits: number;
    unitWeightG: number;
    totalKg: number;
    batchCode: string;
  }>;
  packagingRows: Array<{
    id: number;
    bottlingLineId: number | null;
    productId: number;
    productName: string;
    qtyUnits: number;
    batchCode: string | null;
    legacyMaterialId: number | null;
    notes: string | null;
  }>;
}

export const bottlingApi = {
  meta: () => api.get<BottlingMeta>('/bottling/meta'),
  list: (year: number) => api.get<{ bottlings: BottlingListRow[] }>(`/bottling?year=${year}`),
  get: (id: number) => api.get<BottlingDetails>(`/bottling/${id}`),
  create: (payload: {
    bottlingDate: string;
    sourceContainers: Array<{
      containerId: number;
      qtyKg: number;
    }>;
    targetSiteId: number;
    lines: Array<{
      productId: number;
      qtyUnits: number;
      unitWeightG: number;
      packagingBatchKey: string;
      packagingBatchCode: string | null;
    }>;
    notes?: string;
  }) => api.post<{ id: number; bottlingCode: string }>('/bottling', payload),
  update: (id: number, payload: {
    bottlingDate: string;
    sourceContainers: Array<{
      containerId: number;
      qtyKg: number;
    }>;
    targetSiteId: number;
    lines: Array<{
      productId: number;
      qtyUnits: number;
      unitWeightG: number;
      packagingBatchKey: string;
      packagingBatchCode: string | null;
    }>;
    notes?: string;
  }) => api.patch<{ ok: boolean; id: number; bottlingCode: string }>(`/bottling/${id}`, payload),
  post: (id: number) => api.post<{ ok: boolean; consumeTxId: number; produceTxId: number }>(`/bottling/${id}/post`),
};
